import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usersAPI, exportsAPI } from '../services/api';
import { personName } from '../utils/person';
import PersonAvatar from './PersonAvatar';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './People.css';

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 15,
    total: 0,
  });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const loadUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };
      if (search) params.search = search;
      if (filter === 'influencer') params.is_influencer = true;
      if (filter === 'suspended') params.is_suspended = true;

      const response = await usersAPI.getAll(params);
      setUsers(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadUsers(1);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setTimeout(() => loadUsers(1), 100);
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspend this team user?')) return;
    try {
      await usersAPI.suspend(id);
      loadUsers(pagination.page);
    } catch (error) {
      toast.error(
        'Error suspending user: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleUnsuspend = async (id) => {
    try {
      await usersAPI.unsuspend(id);
      loadUsers(pagination.page);
    } catch (error) {
      toast.error(
        'Error unsuspending user: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team user permanently?')) return;
    try {
      await usersAPI.delete(id);
      loadUsers(pagination.page);
    } catch (error) {
      toast.error(
        'Error deleting user: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleEdit = (id) => {
    navigate(`/users/${id}/edit`);
  };

  const handleAdd = () => {
    navigate('/users/new');
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading team users…</div>;
  }

  return (
    <div className="people-page">
      <div className="page-toolbar">
        <p className="page-meta">
          {pagination.total > 0
            ? `${pagination.total} team member${pagination.total === 1 ? '' : 's'}`
            : 'Admin, seller, and influencer accounts'}
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <ExportCsvButton
            exportRequest={exportsAPI.users}
            filters={{
              search: search || undefined,
              is_suspended: filter === 'suspended' ? true : undefined,
            }}
            filename="users.csv"
          />
          <button type="button" className="btn btn-primary" onClick={handleAdd}>
            + Add user
          </button>
        </div>
      </div>

      <div className="search-filter-bar">
        <div className="filter-field filter-field--grow">
          <label htmlFor="users-search">Search</label>
          <input
            id="users-search"
            type="search"
            placeholder="Name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <div className="filter-field">
          <label htmlFor="users-filter">Filter</label>
          <select id="users-filter" value={filter} onChange={handleFilterChange}>
            <option value="">All users</option>
            <option value="influencer">Influencers</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading && users.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      {users.length === 0 ? (
        <div className="people-empty">
          No team users match your filters.
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleAdd}>
              Add user
            </button>
          </div>
        </div>
      ) : (
        <div className="people-list">
          {users.map((user) => {
            const isSuspended = user.suspended_at !== null;
            const name = personName(user);

            return (
              <article
                key={user.id}
                className={`person-card ${isSuspended ? 'person-card--suspended' : ''}`}
              >
                <PersonAvatar person={user} size="md" />

                <div className="person-card-main">
                  <h3 className="person-card-name">{name}</h3>
                  <p className="person-card-email">{user.email}</p>
                  <div className="person-card-meta">
                    {user.is_admin ? (
                      <span className="role-pill admin">Admin</span>
                    ) : null}
                    {user.is_influencer ? (
                      <span className="role-pill influencer">Influencer</span>
                    ) : null}
                    {isSuspended ? (
                      <span className="status-badge status-suspended">Suspended</span>
                    ) : (
                      <span className="status-badge status-active">Active</span>
                    )}
                  </div>
                </div>

                <div className="person-card-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleEdit(user.id)}
                  >
                    Edit
                  </button>
                  {isSuspended ? (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleUnsuspend(user.id)}
                    >
                      Unsuspend
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleSuspend(user.id)}
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(user.id)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pagination.total > pagination.per_page && (
        <Pagination pagination={pagination} onPageChange={loadUsers} />
      )}
    </div>
  );
};

export default Users;
